/* =============================================================================
   VIEWS / ADMIN / ADMIN-ORDER-ENTRY — Bestellung manuell vom Cockpit aufnehmen
   =============================================================================
   v0.76.0-beta / Session 70 / REF-5 (Modular-Split)
   ----------------------------------------------------------------------------
   Zwei-Stufen-Modal:
     1. Auswahl Hier-Essen / Takeaway / Lieferung
     2. (Hier-Essen) Daten → Tisch → Decision (Items oder Walk-In only)
        (Takeaway)   direkt zur Items-Auswahl
        (Lieferung)  Adresse + PLZ-Check → Items
     5. Items via menu-picker
     6. Submit → App.storage.orders.createAdmin → KDS sieht Order sofort als
        'confirmed' (skip 'pending').

   S70-Refactor: 1.345 LOC zerlegt in 4 Sub-Module (state, delivery, dinein,
   submit) — dieses Haupt-Modul enthält Public-API, Modal-Switchboard,
   Service-Auswahl (Step 1), Items-Step (Step 5), Tisch-Picker, Notes/Nav
   und die window-Bindings + Re-Exports.
============================================================================ */

import { htmlEscape, showToast } from '../../utils/helpers.js';
import { closeModal } from '../../utils/dom.js';

// — Sub-Module ————————————————————————————————————————————————
import { _state, getState } from './admin-order-entry-state.js';
import {
  renderDeliveryAddressBlock,
  adminOrderSetDeliveryField,
  adminOrderDeliveryLookupCustomer,
  adminOrderCheckPlz,
  _openQuickAddDeliveryZoneModal
} from './admin-order-entry-delivery.js';
import {
  renderStepDineInData,
  renderStepDineInTable,
  renderStepDineInDecision,
  adminOrderSetDineInField,
  adminOrderDineInLookupCustomer,
  adminOrderDineInProceed,
  adminOrderBackToDineInData,
  adminOrderDineInProceedToDecision,
  adminOrderBackToDineInTable,
  adminOrderDineInProceedToOrder,
  commitWalkInTableAssignment,
  adminOrderWalkInPickerBack,
  submitDineInSeatOnly,
  adminOrderEntrySeatOnly,
  adminOrderSetSeatField,
  adminOrderSeatLookupCustomer,
  submitSeatOnly
} from './admin-order-entry-dinein.js';
import { submitAdminOrder, _findOpenOrderForContainer } from './admin-order-entry-submit.js';
import {
  renderMailOptInBlock, syncMailOptInCheckboxes, applyEmailAutoDefaults,
  ORDER_MAIL_KINDS, ORDER_POSITIVE_KINDS
} from './mail-optin-block.js';

// -----------------------------------------------------------------------------
// Public API: Modal öffnen + State managen
// -----------------------------------------------------------------------------

export function openAdminOrderEntry(opts) {
  opts = opts || {};
  // S61 Phase D Hotfix / S209-HF2 — Pfad-Auswahl:
  //  • dine-in   → Step 2 (Gast-Daten + danach Tisch-Vorschlag/Sharing)
  //  • takeaway  → Step 5 (Items direkt, Pickup-Zeit per Default)
  //  • delivery  → Step 6 (Adresse + PLZ-Check als EIGENER Daten-Step; nach
  //                „Weiter zur Speisekarte" → Step 5 Items). Analog zum
  //                Reservierungs-Flow (erst Daten, dann Speisekarte) — die
  //                Speisekarte (helles Papier) mischt sich so NIE mit dem dunklen
  //                Adress-Formular (Jonas-Direktive 2026-07-04).
  //  • undefined → Step 1 (Service-Type-Auswahl)
  if (opts.serviceType === 'dine-in') {
    _state.step = 2;
  } else if (opts.serviceType === 'delivery') {
    _state.step = 6;
  } else if (opts.serviceType === 'takeaway') {
    _state.step = 5;
  } else {
    _state.step = 1;
  }
  _state.serviceType = opts.serviceType || null;
  _state.tableId = (opts.reservationContext && opts.reservationContext.tableId) || null;
  _state.assignedTableIds = (opts.reservationContext && Array.isArray(opts.reservationContext.assignedTables))
    ? opts.reservationContext.assignedTables.slice()
    : (_state.tableId ? [_state.tableId] : []);
  _state.notes = (opts.reservationContext && opts.reservationContext.notes) || '';
  _state.seatOnly = false;
  _state.deliveryZone = null;
  _state.deliveryAddress = {
    name: '', phone: '', email: '',
    street: '', plz: '', city: '', floor: '', notes: ''
  };
  _resetMailOptIn();
  // S61 — Reservation-Kontext durchreichen. Wenn gesetzt:
  //  - service_type ist dine-in
  //  - tableId/assignedTables sind aus der Reservation übernommen
  //  - guests/guestName/guestPhone werden in _state.dineInForm gespiegelt
  //  - submitAdminOrder schreibt orders.reservation_id = ctx.id zurück
  _state.reservationContext = opts.reservationContext || null;
  if (opts.reservationContext) {
    const ctx = opts.reservationContext;
    _state.dineInForm = {
      guests:      Number(ctx.guests) || 2,
      guestName:   ctx.guestName  || '',
      guestPhone:  ctx.guestPhone || '',
      durationMin: 90,
      notes:       ctx.notes || '',
      customerId:  ctx.customerId || null
    };
    // Bei Reservation-Kontext starten wir direkt am Items-Schritt (Step 5),
    // weil Service-Type und Tisch durch die Reservation festgelegt sind.
    _state.step = 5;
  }
  renderModal();
  document.getElementById('modal-overlay').classList.add('active');
}

/** Convenience: direkt im Delivery-Pfad starten (Quick-Action „Lieferung erfassen"). */
export function openDeliveryOrderEntry() {
  return openAdminOrderEntry({ serviceType: 'delivery' });
}

function renderModal() {
  const modalContent = document.getElementById('modal-content');
  if (!modalContent) return;
  let body;
  switch (_state.step) {
    case 1: body = renderStepService(); break;
    case 2: body = renderStepDineInData(); break;      // S60 W1: Daten (Hier-Essen)
    case 3: body = renderStepDineInTable(); break;     // S60 W1: Tisch-Vorschlag (Hier-Essen)
    case 4: body = renderStepDineInDecision(); break;  // S60 W1: Jetzt bestellen vs Erst Platz
    case 5: body = renderStepItems(); break;            // Items (Hier-Essen jetzt-bestellen / Takeaway / Lieferung)
    case 6: body = renderStepDeliveryData(); break;    // S209-HF2: Liefer-Daten VOR der Speisekarte
    default: body = renderStepService();
  }
  modalContent.innerHTML = body;
  modalContent.classList.add('admin-order-entry-modal');

  // S207-HOTFIX 3 — der Walk-In-Tisch-Step (3) bettet den Picker-Plan ein: nach dem
  // Insert einpassen (fitAllPlans ist idempotent + No-Op auf Steps ohne .plan-fit).
  if (typeof window.fitAllPlans === 'function') window.fitAllPlans(modalContent);

  // Items-Step: menu-picker initialisieren (step 5)
  if (_state.step === 5 && typeof window.initMenuPicker === 'function') {
    // Bei Lieferung: Adresse zuerst, Items kommen nach erfolgreicher PLZ-Prüfung
    if (_state.serviceType !== 'delivery' || _state.deliveryZone) {
      window.initMenuPicker('admin-order-menu-root');
    }
  }
}

// — Step 1: Service-Auswahl ————————————————————————————————

function renderStepService() {
  // S82: 2-Pfad — Hier Essen · Takeaway. Lieferung läuft über den separaten
  // „Lieferung erfassen"-Button im Dashboard (openDeliveryOrderEntry → Adress-
  // + Liefergebiet-Flow), NICHT über dieses Modal (Operator-Verwirrung,
  // Jonas-Befund S82). Die delivery-Tile wurde hier bewusst entfernt.
  return `
    <div class="modal-header">
      <h2>Bestellung aufnehmen</h2>
      <button class="modal-close" onclick="closeAdminOrderEntry()" aria-label="Schließen">×</button>
    </div>
    <p style="color: var(--ink-soft); font-size: .92rem;">
      Wo soll der Gast essen?
    </p>
    <div class="admin-order-svc-grid">
      <button class="admin-order-svc-tile" onclick="adminOrderSelectService('dine-in')">
        <span class="admin-order-svc-icon">🍽️</span>
        <span class="admin-order-svc-label">Walk-In</span>
        <span class="admin-order-svc-desc">Gast am Tisch · auch „erst Platz nehmen" möglich</span>
      </button>
      <button class="admin-order-svc-tile" onclick="adminOrderSelectService('takeaway')">
        <span class="admin-order-svc-icon">🥡</span>
        <span class="admin-order-svc-label">Takeaway</span>
        <span class="admin-order-svc-desc">Theken-/Telefon-Abholbestellung</span>
      </button>
    </div>
  `;
}

export function adminOrderSelectService(type) {
  // S82: delivery bewusst NICHT in der Whitelist — der Lieferungs-Pfad läuft
  // ausschließlich über openDeliveryOrderEntry() (eigener Button). Defense-in-
  // Depth gegen direkten JS-/URL-Aufruf von 'delivery' in diesem Modal.
  if (!['takeaway','dine-in'].includes(type)) return;
  _state.serviceType = type;
  // S60 Block W1 — Hier-Essen-Flow: erst Daten (step 2), dann Tisch (step 3), dann Decision/Items (step 4)
  // Takeaway/Delivery: direkt zu Items (step 5, weil step 2 & 3 für Hier-Essen reserviert).
  if (type === 'dine-in') {
    const state = getState();
    const settings = state.settings || {};
    _state.dineInForm = {
      guests:      2,
      guestName:   '',
      guestPhone:  '',
      durationMin: Number(settings.slotDurationMin) || 90,
      notes:       '',
      customerId:  null
    };
    _state.step = 2;
  } else {
    _state.step = 5;   // Takeaway/Delivery direkt zu Items
  }
  renderModal();
}

// — Step 2: Items (+ ggf. Tisch-Auswahl, wenn dine-in) ——————————

function renderTablePicker() {
  if (_state.serviceType !== 'dine-in') return '';
  const state = getState();
  const tables = (state.tables || []).slice();
  // Sortieren nach Name für Stabilität
  tables.sort((a, b) => (a.name || a.id).localeCompare(b.name || b.id));

  // Aktive dine-in-Orders pro Tisch zählen — Hinweis, wenn Tisch besetzt
  const orders = (state.orders || []).filter(o =>
    o.serviceType === 'dine-in' &&
    ['confirmed', 'preparing', 'ready'].includes(o.status));
  const occupied = new Map();
  for (const o of orders) {
    if (!o.tableId) continue;
    occupied.set(o.tableId, (occupied.get(o.tableId) || 0) + 1);
  }

  const opts = tables.map(t => {
    const isSelected = _state.tableId === t.id;
    const occ = occupied.get(t.id) || 0;
    return `
      <button type="button"
              class="admin-order-table-chip ${isSelected ? 'active' : ''}"
              onclick="adminOrderSetTable('${htmlEscape(t.id)}')"
              title="${t.seats}P${occ > 0 ? ' · aktuell ' + occ + ' offene Order' + (occ > 1 ? 's' : '') : ''}">
        <span class="admin-order-table-name">${htmlEscape(t.name || t.id)}</span>
        <span class="admin-order-table-seats">${t.seats}P</span>
        ${occ > 0 ? '<span class="admin-order-table-occ">●</span>' : ''}
      </button>
    `;
  }).join('');

  return `
    <div class="admin-order-table-section">
      <div class="admin-order-table-head">
        <strong>Tisch auswählen</strong>
        ${_state.tableId ? '<span class="admin-order-table-status">✓ ' + htmlEscape((state.tables.find(t => t.id === _state.tableId) || {}).name || '?') + '</span>' : '<span class="admin-order-table-status hint">noch nichts gewählt</span>'}
      </div>
      <div class="admin-order-table-list">${opts}</div>
    </div>
  `;
}

export function adminOrderSetTable(tableId) {
  _state.tableId = tableId;
  // Nur den Table-Picker-Bereich neu rendern, um Focus etc. nicht zu zerstören
  const section = document.querySelector('.admin-order-table-section');
  if (section) {
    section.outerHTML = renderTablePicker();
  } else {
    renderModal();
  }
}

function renderStepItems() {
  const svc = _state.serviceType;
  // S134c (Befund 2) — bei Reservierungs-Vorbestellung NICHT „Walk-In" titeln.
  const isResContext = !!(_state.reservationContext && _state.reservationContext.id);
  const svcLabel = svc === 'dine-in'  ? (isResContext ? '🍽️ Reservierung' : '🍽️ Walk-In')
                 : svc === 'delivery' ? '🛵 Lieferung'
                 :                      '🥡 Takeaway';

  // S60 W1 — Bei dine-in ist Tisch + Daten schon in Step 2/3 erfasst.
  // Tisch-Info als kompakte Zusammenfassung statt Tisch-Picker.
  const state = getState();
  const f = _state.dineInForm;
  const tableInfo = (svc === 'dine-in' && _state.tableId) ? (() => {
    const t = (state.tables || []).find(t => t.id === _state.tableId);
    const name = (t && t.name) || _state.tableId;
    return `
      <div class="admin-order-summary" style="background:var(--info-soft);color:var(--ink);border-left:3px solid var(--info);padding:8px 12px;margin-bottom:10px;font-size:.88rem;">
        🍽️ Tisch <strong>${htmlEscape(String(name))}</strong> · ${f.guests} Gäste · ${f.durationMin} Min${f.guestName ? ' · ' + htmlEscape(f.guestName) : ''}${f.customerId ? ' · ✓ Stammkunde' : ''}
      </div>`;
  })() : '';

  // S209-HF2: Delivery-Adresse + PLZ + Mail werden jetzt im EIGENEN Daten-Step
  // (Step 6) erfasst. Im Items-Step steht nur noch eine kompakte Zusammenfassung
  // (analog zur Tisch-Info bei dine-in) mit „Adresse ändern" → zurück zu Step 6.
  const deliveryAddrBlock = (svc === 'delivery') ? renderDeliverySummary() : '';
  const takeawayMailBlock = (svc === 'takeaway') ? renderMailOptInBlock({
    kinds: ORDER_MAIL_KINDS,
    email: _state.customerEmail,
    optIn: _state.mailOptIn,
    idPrefix: 'admorder',
    emailHandler: 'adminOrderSetCustomerEmail',
    toggleHandler: 'adminOrderSetMailOptIn',
    showToggles: false  // S88 C: keine Checkboxen — Server leitet Opt-in aus E-Mail ab
  }) : '';

  // Items-Picker wird bei Delivery erst nach erfolgreichem PLZ-Check eingeblendet
  const menuWrap = (svc === 'delivery' && !_state.deliveryZone) ? `
    <div style="text-align:center;color:var(--ink-soft);padding:24px;font-size:.9rem;">
      📍 Bitte erst Adresse eingeben — Lieferzone wird live geprüft.
    </div>` : `
    <div class="admin-order-menu-wrap">
      <div id="admin-order-menu-root"></div>
    </div>`;

  return `
    <div class="modal-header">
      <h2>Bestellung aufnehmen — ${svcLabel}</h2>
      <button class="modal-close" onclick="closeAdminOrderEntry()" aria-label="Schließen">×</button>
    </div>
    <div class="admin-order-entry-body">
      ${tableInfo}
      ${deliveryAddrBlock}
      ${menuWrap}
      ${takeawayMailBlock}
      ${svc === 'delivery' ? '' : `
      <div class="admin-order-notes-row">
        <label>
          <span>Bemerkung (optional)</span>
          <input type="text" id="admin-order-notes"
                 value="${htmlEscape(_state.notes)}"
                 placeholder="z.B. Allergie, Sonderwunsch"
                 oninput="adminOrderSetNotes(this.value)">
        </label>
      </div>`}
    </div>
    <div class="admin-order-foot">
      <button class="btn btn-ghost" onclick="${svc === 'dine-in' ? 'adminOrderBackToDineInTable()' : (svc === 'delivery' ? 'adminOrderBackToDeliveryData()' : 'adminOrderBackToService()')}">← Zurück</button>
      <button class="btn btn-primary" onclick="submitAdminOrder()">
        ✓ Bestellung anlegen &amp; an Küche senden
      </button>
    </div>
  `;
}

// — Step 6: Liefer-Daten (S209-HF2) ————————————————————————————
// Eigener Daten-Step VOR der Speisekarte: Adresse + PLZ-Check + Mail + Bemerkung.
// Komplett im dunklen Theme (kein Menü-Root → kein Papier). „Weiter zur
// Speisekarte" validiert die Pflichtfelder + Zone (Quick-Add bei fehlender Zone)
// und wechselt dann auf Step 5 (Items). Spiegelt den Reservierungs-Flow.

function renderStepDeliveryData() {
  return `
    <div class="modal-header">
      <h2>Bestellung aufnehmen — 🛵 Lieferung · Adresse</h2>
      <button class="modal-close" onclick="closeAdminOrderEntry()" aria-label="Schließen">×</button>
    </div>
    <div class="admin-order-entry-body">
      ${renderDeliveryAddressBlock()}
      <div class="admin-order-notes-row">
        <label>
          <span>Bemerkung (optional)</span>
          <input type="text" id="admin-order-notes"
                 value="${htmlEscape(_state.notes)}"
                 placeholder="z.B. Allergie, Sonderwunsch"
                 oninput="adminOrderSetNotes(this.value)">
        </label>
      </div>
    </div>
    <div class="admin-order-foot">
      <button class="btn btn-ghost" onclick="closeAdminOrderEntry()">Abbrechen</button>
      <button class="btn btn-primary" onclick="adminOrderDeliveryToItems()">
        Weiter zur Speisekarte →
      </button>
    </div>
  `;
}

/** Kompakte Liefer-Zusammenfassung im Items-Step (statt des vollen Formulars). */
function renderDeliverySummary() {
  const a = _state.deliveryAddress || {};
  const z = _state.deliveryZone;
  const addrLine = [a.street, [a.plz, a.city].filter(Boolean).join(' ')].filter(Boolean).join(', ');
  const zoneStr = z ? ` · ${htmlEscape(z.zone_name || z.name || 'Liefergebiet')}` : '';
  return `
    <div class="admin-order-summary" style="background:var(--info-soft);color:var(--ink);border-left:3px solid var(--info);padding:8px 12px;margin-bottom:10px;font-size:.88rem;display:flex;justify-content:space-between;align-items:center;gap:10px;flex-wrap:wrap;">
      <span>🛵 <strong>${htmlEscape(a.name || '—')}</strong>${a.phone ? ' · ' + htmlEscape(a.phone) : ''} · ${htmlEscape(addrLine || '—')}${a.floor ? ' · ' + htmlEscape(a.floor) : ''}${zoneStr}</span>
      <button class="btn btn-ghost btn-sm" onclick="adminOrderBackToDeliveryData()" style="margin:0;">✎ Adresse ändern</button>
    </div>`;
}

/** Pure (Vitest): Vorflug-Check für den Liefer-Daten-Step. Spiegelt die
 *  Pflichtfeld-/Zone-Checks aus submitAdminOrder, damit sie schon VOR der
 *  Speisekarte greifen. `needsZone` signalisiert den Quick-Add-Pfad. */
export function _deliveryDataReady(state) {
  const a = (state && state.deliveryAddress) || {};
  if (!a.name || !a.phone || !a.street || !a.plz || !a.city) {
    return { ok: false, error: 'Bitte alle Pflichtfelder der Lieferadresse füllen.' };
  }
  if (!((state && state.customerEmail) || '').trim()) {
    return { ok: false, error: 'Bitte E-Mail für die Lieferung angeben (Bestätigung + Storno-Link).' };
  }
  if (!(state && state.deliveryZone)) {
    return { ok: false, needsZone: true, error: '⚠️ PLZ nicht im Liefergebiet.' };
  }
  return { ok: true };
}

/** „Weiter zur Speisekarte" — validiert die Liefer-Daten und wechselt auf Step 5.
 *  Bei fehlender Zone + settings.rules-Recht: Quick-Add-Zone-Modal (wie Submit). */
export function adminOrderDeliveryToItems() {
  const check = _deliveryDataReady(_state);
  if (!check.ok) {
    if (check.needsZone) {
      const plz = ((_state.deliveryAddress && _state.deliveryAddress.plz) || '').trim();
      const canAddZone = window.App && window.App.auth
        && typeof window.App.auth.canEdit === 'function'
        && window.App.auth.canEdit('settings.rules');
      if (canAddZone && plz && /^\d{4,5}$/.test(plz)) {
        _openQuickAddDeliveryZoneModal(plz);
        return;
      }
      return showToast('⚠️ PLZ nicht im Liefergebiet. Bitte Manager/Owner kontaktieren oder in Settings → Lieferung → Zonen ergänzen.', 'error');
    }
    return showToast(check.error, 'error');
  }
  _state.step = 5;
  renderModal();
}

/** Zurück von der Speisekarte zum Liefer-Daten-Step (Adresse ändern). */
export function adminOrderBackToDeliveryData() {
  _state.step = 6;
  renderModal();
}

// — Nav + Notes ———————————————————————————————————————————————

export function adminOrderBackToService() {
  _state.step = 1;
  // tableId beibehalten, falls User nur zurück zur Service-Wahl will
  renderModal();
}

export function adminOrderSetNotes(v) {
  _state.notes = String(v || '');
}

// — REF-7b (S75): Cockpit-Mail-Opt-In ————————————————————————————
// E-Mail-Eingabe rechnet die Auto-Defaults neu (confirmed+ready an, sobald eine
// E-Mail da ist) und synchronisiert NUR die Checkboxen — das E-Mail-Input wird
// NICHT neu gerendert, damit der Cursor/Focus beim Tippen erhalten bleibt.
export function adminOrderSetCustomerEmail(v) {
  _state.customerEmail = String(v || '');
  _state.mailOptIn = applyEmailAutoDefaults(_state.mailOptIn, _state.customerEmail, ORDER_POSITIVE_KINDS);
  syncMailOptInCheckboxes('admorder', _state.mailOptIn, !!_state.customerEmail.trim());
}

export function adminOrderSetMailOptIn(kind, checked) {
  if (!_state.mailOptIn || !(kind in _state.mailOptIn)) return;
  _state.mailOptIn[kind] = !!checked;
  _state.mailOptIn._touched = true;
}

/** Mail-Opt-In-State zurücksetzen (Open + Close). */
function _resetMailOptIn() {
  _state.customerEmail = '';
  _state.mailOptIn = { confirmed: false, ready: false, cancelled: false, receipt: false, _touched: false };
}

// — Close ———————————————————————————————————————————————————

export function closeAdminOrderEntry() {
  closeModal();
  _state.step = 1;
  _state.serviceType = null;
  _state.tableId = null;
  _state.assignedTableIds = [];
  _state.notes = '';
  _state.seatOnly = false;
  _state.dineInForm = {
    guests: 2, guestName: '', guestPhone: '',
    durationMin: 90, notes: '', customerId: null
  };
  _state.deliveryZone = null;
  _state.deliveryAddress = {
    name: '', phone: '', email: '',
    street: '', plz: '', city: '', floor: '', notes: ''
  };
  _resetMailOptIn();
  // S60 W4.1 — Cross-Link-Marker zurücksetzen, sonst kann eine nächste Order
  // fälschlich mit einem alten Walk-In verknüpft werden (Order-zu-falscher-Tisch-Bug).
  if (typeof window !== 'undefined') window._pendingOccupancyIdForOrder = null;
  // S61 — Reservation-Context auch zurücksetzen, damit nächste Order
  // nicht versehentlich mit der vorherigen Reservation verknüpft wird.
  _state.reservationContext = null;
  // Modal-Klasse wieder entfernen
  const modalContent = document.getElementById('modal-content');
  if (modalContent) modalContent.classList.remove('admin-order-entry-modal');
}

// — Re-Exports der Sub-Module (Backward-Compat für externe Importer) ——————

export {
  // state
  _state,
  getState,
  // S82: Service-Picker-Renderer für Vitest (pure Template-Funktion)
  renderStepService,
  // delivery
  renderDeliveryAddressBlock,
  adminOrderSetDeliveryField,
  adminOrderDeliveryLookupCustomer,
  adminOrderCheckPlz,
  _openQuickAddDeliveryZoneModal,
  // dinein
  renderStepDineInData,
  renderStepDineInTable,
  renderStepDineInDecision,
  adminOrderSetDineInField,
  adminOrderDineInLookupCustomer,
  adminOrderDineInProceed,
  adminOrderBackToDineInData,
  adminOrderDineInProceedToDecision,
  adminOrderBackToDineInTable,
  adminOrderDineInProceedToOrder,
  commitWalkInTableAssignment,
  adminOrderWalkInPickerBack,
  submitDineInSeatOnly,
  adminOrderEntrySeatOnly,
  adminOrderSetSeatField,
  adminOrderSeatLookupCustomer,
  submitSeatOnly,
  // submit
  submitAdminOrder
};

// — Backward-Compat: window-Bindings ————————————————————————————
// Inline-Handler (onclick="…") in den gerenderten Modals greifen über window.*.
// renderAdminOrderEntryModal ist intern für Sub-Module (delivery/dinein),
// damit sie das gesamte Modal neu rendern können, ohne Circular-Import.
if (typeof window !== 'undefined') {
  Object.assign(window, {
    openAdminOrderEntry,
    openDeliveryOrderEntry,        // S60 Quick-Action „Lieferung erfassen"
    adminOrderSelectService,
    adminOrderSetTable,
    adminOrderSetNotes,
    adminOrderSetCustomerEmail,   // REF-7b Cockpit-Mail-Opt-In
    adminOrderSetMailOptIn,       // REF-7b Cockpit-Mail-Opt-In
    adminOrderBackToService,
    adminOrderSetDeliveryField,    // S60 Delivery-Adress-Felder
    adminOrderCheckPlz,            // S60 PLZ-Live-Check
    adminOrderDeliveryLookupCustomer, // S60 W4.7 — Stammkunden-Lookup im Liefer-Pfad
    adminOrderDeliveryToItems,     // S209-HF2 — „Weiter zur Speisekarte"
    adminOrderBackToDeliveryData,  // S209-HF2 — „Adresse ändern"
    adminOrderEntrySeatOnly,         // S60 (legacy stub)
    adminOrderSetSeatField,          // S60 (legacy stub)
    adminOrderSeatLookupCustomer,    // S60 (legacy stub)
    submitSeatOnly,                  // S60 (legacy stub)
    // S60 W1 — Hier-Essen 3-Step-Flow
    adminOrderSetDineInField,
    adminOrderDineInLookupCustomer,
    adminOrderDineInProceed,
    adminOrderBackToDineInData,
    adminOrderDineInProceedToDecision,
    adminOrderBackToDineInTable,
    adminOrderDineInProceedToOrder,
    submitDineInSeatOnly,
    // S134b Hotfix — die zwei neuen P2-Picker-Handler fehlten im window-Block:
    // dom-bindings löst Inline-Handler gegen window auf; _commitPicker guarded
    // `typeof window.commitWalkInTableAssignment === 'function'` → silent no-op
    // statt Fehler (Cowork-UI-Test-Befund 2026-06-12).
    commitWalkInTableAssignment,
    adminOrderWalkInPickerBack,
    submitAdminOrder,
    // S196 Cut b1 — Warenkorb-Kontextbutton (Belegungs-Modal) braucht die
    // „eine offene Order pro Tisch"-Ermittlung als Single-Source.
    _findOpenOrderForContainer,
    closeAdminOrderEntry,
    // Internal: Sub-Module rufen das auf, wenn sie das Modal neu rendern müssen
    renderAdminOrderEntryModal: renderModal
  });
}
